import React, { useState, useEffect } from 'react';
import axios from '../util/axiosConfig';
import { Table, Row, Col, Button, Typography, Input, Form, message, Collapse, Card, Select, Flex, Checkbox } from 'antd';
import UploadVendor from './UploadVendor'
import '../styles/reports.css'
import { useInvContext } from '../context/InvContext';
import ViewUpload from './ViewUpload';
import { UpOutlined, DownOutlined } from '@ant-design/icons';

const { TextArea } = Input;
const { Text } = Typography;
const { Option } = Select;


const VendorEdit: React.FC = () => {
    const { vendorDoc } = useInvContext();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [lineItems, setLineItems] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [disableSAP, setDisableSAP] = useState(true);
    const [form] = Form.useForm();
    // for panel Headers
    const [panelID, setPanelID] = useState<any>();
    const [token, setToken] = useState<any>(); //set from url
    const [invoiceData, setInvoiceData] = useState<any>({});
    const [disputeReasons, setDisputeReasons] = useState<any>([]);
    const [disputeReasonID, setdisputeReasonID] = useState<any>();
    const [rejectionRemark, setRejectionRemark] = useState<any>();
    const [isEdit, setIsEdit] = useState(false);
    const [isRemarks, setIsRemarks] = useState(false);

    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const query = queryParams.get('q');
        if (query) {
            const [tokenPart, panelIDPart] = query.split('=');
            const modifiedToken = tokenPart.slice(0, -1);
            setToken(modifiedToken);
            setPanelID(panelIDPart);
        }
    }, []);

    useEffect(() => {
        if (panelID) {
            fetchInvoiceData(panelID);
            fetchDisputeReasons();
        }
        // eslint-disable-next-line
    }, [panelID]);


    //fetch Panel Headers Data
    const fetchInvoiceData = async (id: number) => {
        try {
            const response = await axios.get(`/get-invoice-dto`, {
                params: {
                    invoiceId: id
                },
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (response?.data) {
                setInvoiceData(response?.data?.data);
                setRejectionRemark(response?.data?.data?.rejectionRemark);
            }
        } catch (error) {
            console.error('Failed to fetch invoice data:', error);
            message.error("The URL is not valid!");
        }
    };

    const fetchDisputeReasons = async () => {
        try {
            const response = await axios.get('/dispute-reason/vendor-dispute-reason-list', {
                params: {
                    invoiceId: panelID || null
                },
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });
            if (response?.data?.data) {
                setDisputeReasons(response.data.data);
            }
        } catch (error) {
            console.error('Failed to fetch dispute reasons:', error);
        }
    };

    useEffect(() => {
        const value = disputeReasons[0]?.id
        handleDisputeReasonChange(value);
    }, [disputeReasons])

    const handleDisputeReasonChange = (value: any) => {
        if (value === 1 || value === 2 || value === 4 || value === 5) {
            setIsEdit(true);
            setIsRemarks(false);
        } else if (value === 3) {
            setIsEdit(false);
            setIsRemarks(true);
        } else {
            setIsEdit(false);
            setIsRemarks(false);
        }
        setdisputeReasonID(value);
    };

    const paginationConfig = {
        current: page,
        pageSize: pageSize,
        total: total,
        showTotal: (total: number, range: [number, number]) => `Showing ${range[0]}-${range[1]} of ${total} entries`,
        showSizeChanger: true,
        pageSizeOptions: ["5", "10", "20", "50"],
        locale: { items_per_page: '' },
        onChange: (newPage: number, newPageSize: number) => {
            setPage(newPage);
            setPageSize(newPageSize);
        },
    };

    const fetchPanelData = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`/get-line-items`, {}, {
                params: {
                    pageSize: pageSize,
                    pageNo: page - 1,
                    invoiceId: panelID,
                },
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.data?.error === null) {
                const content = response?.data?.data?.content || [];
                setLineItems(content);
                setTotal(response?.data?.data?.totalElements);
                form.setFieldsValue({ lineItems: content });
            } else {
                console.error(response.data?.error, 4);
            }
        } catch (error) {
            console.error('Failed to fetch line item data');
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        if (panelID) {
            fetchPanelData();
        }
        // eslint-disable-next-line
    }, [page, pageSize, panelID]);

    useEffect(() => {
        if (isEdit || isRemarks) {
            setDisableSAP(false);
        }
        // eslint-disable-next-line
    }, [isEdit, isRemarks]);

    useEffect(() => {
        form.setFieldsValue({ remarks: isRemarks ? rejectionRemark || '' : '' });
        // eslint-disable-next-line
    }, [disputeReasonID, rejectionRemark, isRemarks]);

    const handleUpdateClick = async () => {
        try {
            const values = await form.validateFields();
            const { lineItems: formLineItems, remarks } = values;

            if (isRemarks && !remarks) {
                message.error("Please enter rejection remarks before updating.");
                return;
            }

            // if (isEdit) {
            //     if (!Array.isArray(formLineItems) || formLineItems.length === 0) {
            //         message.error("Please enter all required values for at least one item.");
            //         return;
            //     }

            //     const hasValidItem = formLineItems.some(item =>
            //         item?.inputQuantity !== undefined && item?.inputRate !== undefined && item?.inputAmountPrice !== undefined
            //     );

            //     if (!hasValidItem) {
            //         message.error("Please fill in all required fields for at least one item.");
            //         return;
            //     }
            // }

            const updatedLineItems = (lineItems)?.map((originalItem: any, index: number) => {
                const formItem = formLineItems?.[index] || {};

                return {
                    ...originalItem,
                    itemReference: formItem?.itemReference ?? originalItem.itemReference,
                    description: formItem?.description ?? originalItem.description,
                    hsn: formItem?.hsn ?? originalItem.hsn,
                    quantity: formItem?.quantity ?? originalItem.quantity,
                    rate: formItem?.rate ?? originalItem.rate,
                    price: formItem?.price ?? originalItem.price,
                    inputQuantity: formItem?.inputQuantity ?? originalItem.inputQuantity,
                    inputRate: formItem?.inputRate ?? originalItem.inputRate,
                    inputAmountPrice: formItem?.inputAmountPrice ?? originalItem.inputAmountPrice,
                    grnItemDTOList: originalItem.grnItemDTOList?.map((subItem: any) => ({
                        ...subItem,
                        selectedFlag: subItem.selectedFlag
                    })),
                };
            });

            const finalRemarks = isRemarks ? remarks || '' : rejectionRemark || '';
            const fileDetailsIdList = vendorDoc.map(item => item.fileDetailId);

            const queryParams = new URLSearchParams({
                invoiceId: panelID,
                rejectionRemark: finalRemarks || '',
                disputeReasonId: disputeReasonID,
            });
            fileDetailsIdList.forEach((id: any) => queryParams.append('fileDetailsIdList', id));

            console.log(fileDetailsIdList)
            const response = await axios.post(`/update-line-items-list?${queryParams.toString()}`, updatedLineItems, {
                // params: {
                //     invoiceId: panelID,
                //     rejectionRemark: finalRemarks || '',
                //     disputeReasonId: disputeReasonID,
                //     fileDetailsIdList: [23, 34]
                // },
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.data?.error === null) {
                message.success("Line items updated successfully.");
                resetDisputeReason(finalRemarks);
                setDisableSAP(false);
                fetchPanelData();
            } else {
                message.error("Failed to update line items.");
            }
        } catch (error) {
            console.error("Validation failed or update error", error);
            message.error("The URL is not valid!");
            setDisableSAP(true);
        }
    };

    const resetDisputeReason = async (finalRemarks: any) => {
        try {
            const payload = {
                invoicePageableDto: {
                    id: panelID || 0,
                    reference: invoiceData.reference || '',
                    subject: invoiceData.subject || '',
                    invoiceReference: invoiceData.invoiceReference || '',
                    companyReference: invoiceData.companyReference || '',
                    currencyCode: invoiceData.currencyCode || '',
                    cfPostingDate: invoiceData.cfPostingDate || '',
                    invoiceDate: invoiceData.invoiceDate || '',
                    billingAddressReference: invoiceData.billingAddressReference || '',
                    subTotal: invoiceData.subTotal || 0,
                    poDate: invoiceData.poDate || '',
                    poNumber: invoiceData.poNumber || '',
                    status: invoiceData.status || '',
                    vendorName: invoiceData.vendorName || '',
                    taxableValue: invoiceData.taxableValue || 0,
                    taxAmount: parseFloat(invoiceData.taxAmount) || 0,
                    totalAmount: parseFloat(invoiceData.totalAmount) || 0,
                    fileDetailsId: invoiceData.fileDetailsId || 0,
                    s3Url: invoiceData.s3Url || '',
                    rejectionRemark: finalRemarks || invoiceData.rejectionRemark || '',
                    vendorUploadDtos: invoiceData?.vendorUploadDtos || []
                },
                lineItemPageableDtos: []
            };

            await axios.post('/update-invoice', payload, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            if (panelID) {
                fetchInvoiceData(panelID);
            }
        } catch (error) {
            console.error('Failed to update invoice:', error);
            message.error("The URL is not valid!");
        }

        setdisputeReasonID(undefined);
        handleDisputeReasonChange(null);
    };

    const handleSendClick = async (e: any) => {
        e.stopPropagation();
        try {
            setLoading(true)
            const response = await axios.put(`/send-single-invoice-to-sap`, {}, {
                params: {
                    id: panelID
                },
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (response.data?.error === null) {
                message.success("Sent to SAP")
                setDisableSAP(true);
            } else {
                console.error('API error:', response.data?.error);
            }
        } catch (error) {
            console.error('Failed to make API call:', error);
            message.error("The URL is not valid!");
        } finally {
            setLoading(false)
        }
    };

    const preventMinus = (e: any) => {
        if (e.code === 'Minus' || e.code === 'Equal') {
            e.preventDefault();
        }
    };

    const renderPanelHeader = () => (
        <>
            <Row>
                <Col span={24} className='align-center-div'>
                    <Row className="header-list-row" style={{ width: '100%' }}>
                        <Col span={3}>
                            <p className='panel-head-txt'>Invoice No</p>
                            <p className='panel-text'>{invoiceData?.invoiceReference || '-'}</p>
                        </Col>
                        <Col span={4}>
                            <p className='panel-head-txt'>Vendor Name</p>
                            <p className='panel-text'>{invoiceData?.vendorName || '-'}</p>
                        </Col>
                        <Col span={3}>
                            <p className='panel-head-txt'>PO Number</p>
                            <p className='panel-text'>{invoiceData?.poNumber || '-'}</p>
                        </Col>
                        <Col span={2}>
                            <p className='panel-head-txt'>Tax Amount</p>
                            <p className='panel-text'>{invoiceData?.taxAmount || '-'}</p>
                        </Col>
                        <Col span={3}>
                            <p className='panel-head-txt'>Total Amount</p>
                            <p className='panel-text'>{invoiceData?.totalAmount || '-'}</p>
                        </Col>
                        <Col span={4}>
                            <p className='panel-head-txt'>Status</p>
                            <p
                                className='panel-text'
                                style={{
                                    color: typeof invoiceData?.status === 'string' && invoiceData?.status.toLowerCase().startsWith('success') ? 'green' :
                                        invoiceData?.status === '' ? 'blue' : 'red'
                                }}
                            >
                                {invoiceData?.status ? invoiceData.status : "Send to SAP"}
                            </p>
                        </Col>
                        <Col span={3}>
                            <p className='panel-head-txt'>Dispute Reason</p>
                            <Select
                                placeholder="Select a reason"
                                onChange={handleDisputeReasonChange}
                                value={disputeReasonID}
                                style={{ width: '100%' }}
                            >
                                {disputeReasons.map((reason: any) => (
                                    <Option key={reason?.id} value={reason?.id}>
                                        {reason?.name}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                    </Row>
                </Col>
            </Row>
            {(invoiceData?.vendorUploadDtos && !isEdit) && (
                <Row>
                    <Col span={24}>
                        <Flex align='center' justify='flex-start'>
                            <ViewUpload fileList={invoiceData?.vendorUploadDtos} />
                        </Flex>
                    </Col>
                </Row>
            )}
            {isEdit && (
                <Row>
                    <Col span={24}>
                        <Flex align='center' justify='flex-start'>
                            <UploadVendor accessToken={token} />
                        </Flex>
                    </Col>
                </Row>
            )}
        </>
    );

    const columns: any = [
        {
            title: 'S.NO',
            dataIndex: 'itemReference',
            key: 'itemReference',
            width: 120
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            className: 'des-column',
            width: 200
        },
        {
            title: 'HSN/SAC',
            dataIndex: 'hsn',
            key: 'hsn'
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity'
        },
        {
            title: 'Rate',
            dataIndex: 'rate',
            key: 'rate'
        },
        {
            title: 'Price',
            dataIndex: 'price',
            key: 'price'
        },
        {
            title: 'Dispute Issue',
            dataIndex: 'disputeError',
            key: 'disputeError',
            width: 150
        },
        {
            title: 'Input Quantity',
            dataIndex: 'inputQuantity',
            key: 'inputQuantity',
            render: (text: any, record: any, index: any) => (isEdit && record?.disputeError !== "Success") ? (
                <Form.Item
                    name={['lineItems', index, 'inputQuantity']}
                    rules={[
                        { required: false, message: 'Please enter input quantity' },
                    ]}
                    style={{ margin: 0 }}
                >
                    <Input type="number" value={text === 0 ? '' : text} min={0} onKeyDown={preventMinus} />
                </Form.Item>
            ) : (
                <span>{text === 0 ? '' : text}</span>
            ),
        },
        {
            title: 'Input Rate',
            dataIndex: 'inputRate',
            key: 'inputRate',
            render: (text: any, record: any, index: any) => (isEdit && record?.disputeError !== "Success") ? (
                <Form.Item
                    name={['lineItems', index, 'inputRate']}
                    rules={[
                        { required: false, message: 'Please enter input rate' },
                    ]}
                    style={{ margin: 0 }}
                >
                    <Input type="number" value={text === 0 ? '' : text} min={0} onKeyDown={preventMinus} />
                </Form.Item>
            ) : (
                <span>{text === 0 ? '' : text}</span>
            ),
        },
        {
            title: 'Input Amount Price',
            dataIndex: 'inputAmountPrice',
            key: 'inputAmountPrice',
            render: (text: any, record: any, index: any) => (isEdit && record?.disputeError !== "Success") ? (
                <Form.Item
                    name={['lineItems', index, 'inputAmountPrice']}
                    rules={[
                        { required: false, message: 'Please enter input amount price' },
                    ]}
                    style={{ margin: 0 }}
                >
                    <Input type="number" value={text === 0 ? '' : text} min={0} onKeyDown={preventMinus} />
                </Form.Item>
            ) : (
                <span>{text === 0 ? '' : text}</span>
            ),
        },
    ];

    const subItemColumns = (parentRecord: any) => [
        {
            title: '',
            key: 'select',
            render: (text: any, record: any) => (
                (disputeReasonID === 5 && parentRecord?.disputeError !== "Success") ? (
                    <Checkbox
                        checked={record.selectedFlag}
                        onChange={(e) => handleCheckboxChange(e, record)}
                    />
                ) : (
                    <Checkbox
                        checked={record.selectedFlag}
                        disabled
                    />
                )
            ),
            width: 50
        },
        {
            title: 'GRN/SES No',
            dataIndex: 'grnno',
            key: 'grnno',
            width: 150
        },
        {
            title: 'GRN/SES Item No',
            dataIndex: 'grnitemno',
            key: 'grnitemno',
            width: 150
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            width:250
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            width: 150
        },
        {
            title: 'Rate',
            dataIndex: 'rate',
            key: 'rate',
            width: 150
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            width: 120
        }
    ];

    const handleCheckboxChange = (e: any, record: any) => {
        const { checked } = e.target;
        const { miroLineItemId, id } = record;

        if (!miroLineItemId) {
            console.error('Invalid miroLineItemId');
            return;
        }

        setLineItems(prevLineItems => {
            return prevLineItems?.map(lineItem => {
                if (lineItem?.miroLineItemId === miroLineItemId) {
                    const updatedGrnDTOList = lineItem?.grnItemDTOList?.map((subItem: any) =>
                        subItem.id === id ? { ...subItem, selectedFlag: checked } : subItem
                    );

                    return {
                        ...lineItem,
                        grnItemDTOList: updatedGrnDTOList
                    };
                }
                return lineItem;
            });
        });
    };

    return (
        <Card bordered={false} title={'Edit - Invoice'}>
            <Collapse
                activeKey={panelID}
                bordered={false}
                accordion={true}
                className='mt-2 single-edit-collapse'
            >
                <Collapse.Panel
                    header={renderPanelHeader()}
                    key={invoiceData?.id?.toString()}
                >
                    <div>
                        <Form form={form} name="lineItemForm" layout="vertical" initialValues={{ lineItems, remarks: rejectionRemark }}>
                            {isRemarks ? (
                                <Row>
                                    <Col span={24}>
                                        <Form.Item
                                            label="Enter Remarks for Rejection"
                                            name="remarks"
                                            rules={[{ required: isRemarks, message: 'Please enter rejection remarks' }]}
                                        >
                                            <TextArea rows={3} placeholder="Enter your text here" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            ) : (
                                rejectionRemark && (
                                    <Row>
                                        <Col span={24}>
                                            <Text className="rejection-remark-text">
                                                <span style={{ fontWeight: 'bold' }}>Rejection Remarks : </span> {rejectionRemark}
                                            </Text>
                                        </Col>
                                    </Row>
                                )
                            )}
                            <br />
                            <Text className='totalcount' type='secondary'>Total No Records : {lineItems.length}</Text>
                            <Table
                                loading={loading}
                                id="lineItemTable"
                                className="lineItemEditTable"
                                dataSource={lineItems}
                                size={'small'}
                                rowKey={(record: any) => record.id}
                                columns={columns}
                                pagination={paginationConfig}
                                scroll={{ x: 'max-content' }}
                                expandable={{
                                    expandedRowRender: (record) => (
                                        <Table
                                            id="subTable"
                                            columns={subItemColumns(record)}
                                            dataSource={record.grnItemDTOList || []}
                                            pagination={false}
                                            size={'small'}
                                            rowKey={(subRecord: any) => subRecord.id}
                                            scroll={{ x: 150 }}
                                        />
                                    ),
                                    expandIcon: ({ expanded, onExpand, record }) =>
                                        expanded ? (
                                            <UpOutlined onClick={(e) => onExpand(record, e)} />
                                        ) : (
                                            <DownOutlined onClick={(e) => onExpand(record, e)} />
                                        ),
                                }}
                            />
                            <Row justify="start" >
                                {(lineItems.length > 0 && (isEdit || isRemarks)) && (
                                    <Col style={{ marginTop: '-40px', marginLeft: '2px' }}>
                                        <Button type="primary" style={{ marginRight: '10px' }} onClick={handleUpdateClick}>
                                            Update
                                        </Button>
                                    </Col>
                                )}
                                {!disableSAP && (
                                    <Col style={{ marginTop: '-40px', marginLeft: '2px' }}>
                                        <Button disabled={isEdit || isRemarks} onClick={(e) => { handleSendClick(e) }}>
                                            Send to SAP
                                        </Button>
                                    </Col>
                                )}
                            </Row>
                        </Form>
                    </div>
                </Collapse.Panel>
            </Collapse>
        </Card>
    );
}

export default VendorEdit;